<template>
  <div
    class="flex relative bg-neutral-background-secondary px-7.5 py-5 space-x-6"
  >
    <div
      v-if="review.includedReviewItems.value.length"
      class="h-full flex flex-1 items-start"
    >
      <div
        class="inline-grid grid-cols-1 w-1/2 border border-neutral-border-default rounded-l-lg"
      >
        <div
          class="bg-neutral-background-default h-12.5 rounded-tl-lg sticky top-13 left-0 z-51"
        >
          <div
            class="h-full text-sm px-5 py-3.5 justify-between border-neutral-border-default border-b flex items-center font-medium"
          >
            <span class="text-neutral-text-title">Records</span>
            <div class="flex items-center gap-2">
              <Sort />
              <ReviewItemFilters />
            </div>
          </div>
        </div>

        <template v-if="paginatedIncludedReviewItems.length">
          <ReviewItemDetails
            v-for="(reviewItem, index) in paginatedIncludedReviewItems"
            :key="reviewItem.id"
            :expansion-panel="expansionPanels[index]"
            :is-metadata-expanded="isMetadataExpanded"
            :review-item="reviewItem"
          />
        </template>
        <div v-else class="p-4 text-center text-gray-500">
          No articles match your filters
        </div>
      </div>
      <div
        class="bg-neutral-background-default w-1/2 h-12.5 sticky top-13 left-0 z-40 rounded-tr-lg"
      >
        <div
          :title="sortedAttributes[selectedAttributeIndex].question"
          class="h-full text-sm border-t border-r border-b border-neutral-border-default text-neutral-text-title flex p-3.5 items-center font-medium justify-between"
        >
          <div class="truncate">
            {{ sortedAttributes[selectedAttributeIndex].label }} - Synthesis
          </div>
        </div>
        <div class="bg-white">
          <AttributeSynthesis
            :key="sortedAttributes[selectedAttributeIndex].id"
            :attribute-structure="sortedAttributes[selectedAttributeIndex]"
            class="border-b border-r border-neutral-border-default"
          />
        </div>
      </div>
    </div>
    <div v-else-if="review.entity.value?.studies?.length" class="h-full">
      No articles have been included
    </div>
    <div v-else>This review is empty</div>

    <div
      v-if="paginatedIncludedReviewItems.length"
      class="w-[260px] z-1 sticky flex flex-col top-13 right-0 h-[calc(100vh-80px)] overflow-y-auto items-start gap-3"
    >
      <button
        v-for="(attribute, index) in sortedAttributes"
        :key="attribute.id"
        class="px-3.5 py-2 w-full text-left rounded-lg transition-colors flex justify-between hover:bg-white hover:shadow hover:shadow-black/10"
        :class="{
          'bg-white shadow shadow-black/10 text-black':
            index === selectedAttributeIndex,
          'text-gray-400': index !== selectedAttributeIndex,
        }"
        @click="selectedAttributeIndex = index"
      >
        <span
          :title="attribute.label"
          class="text-base font-medium whitespace-nowrap truncate"
        >
          {{ attribute.label }}
        </span>
        <CheckCircleIcon
          v-if="attributeSynthesisFinalizationState[index]"
          class="size-6 text-green-500 shrink-0"
        />
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { injectStrict } from '@app/utils/injectStrict'
import { useRoute, useRouter } from 'vue-router'
import { useStorage } from '@vueuse/core'

import { CheckCircleIcon } from '@heroicons/vue/16/solid'
import { ReviewKey } from '../../use-review'
import Sort from '../Sort/Sort.vue'
import ReviewItemDetails from '../ReviewItemDetails/ReviewItemDetails.vue'
import AttributeSynthesis from './AttributeSynthesis.vue'
import useExpansionPanel from '@app/composables/use-expansion-panel'
import ReviewItemFilters from '../SearchAndFilters/ReviewItemFilters.vue'

const review = injectStrict(ReviewKey)
const route = useRoute()
const router = useRouter()

const currentPage = computed({
  get() {
    return Number(route.query.page) || 1
  },
  set(value: number) {
    router.replace({
      query: {
        ...route.query,
        page: value.toString(),
      },
    })
  },
})

watch(
  currentPage,
  (newPage) => {
    router.replace({
      query: {
        ...route.query,
        page: newPage.toString(),
      },
    })
  },
  {
    immediate: true,
  },
)

const itemsPerPage = computed({
  get() {
    return Number(route.query.itemsPerPage) || 10
  },
  set(value: number) {
    router.replace({
      query: {
        ...route.query,
        itemsPerPage: value.toString(),
      },
    })
  },
})

const paginatedIncludedReviewItems = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return review.filteredIncludedReviewItems.value.slice(
    start,
    start + itemsPerPage.value,
  )
})

const sortedAttributes = computed(() =>
  review.entity.value.plan!.synthesisPlan!.attributesStructure.toSorted(
    (a, b) => {
      if (a.order === null && b.order === null) return 0
      if (a.order === null) return -1
      if (b.order === null) return 1
      return a.order > b.order ? 1 : -1
    },
  ),
)

const attributeSynthesisFinalizationState = computed(() => {
  return sortedAttributes.value.map((attribute) => {
    const attributeId = attribute.id

    const matchingPlanAttribute =
      review.entity.value.plan?.synthesisPlan.attributesStructure.find(
        (planAttr) => planAttr.id === attributeId,
      )

    return matchingPlanAttribute?.isSynthesisFinalized === true
  })
})

const selectedAttributeIndex = ref(0)

const isMetadataExpanded = useStorage(
  'isMetadataExpanded-synthesis-' + review.entity.value.id,
  true,
)

const expansionPanels = computed(() =>
  paginatedIncludedReviewItems.value.map(() => useExpansionPanel()),
)
</script>
